import React, { useEffect, useState } from "react";
import { useData } from "../context/DataContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Link } from "react-router-dom";
import { Filter, Link2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Loader2, Search } from "lucide-react";
import { Activity, Procedure, User, ProcedureLink } from "../types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Pagination, PaginationContent, PaginationItem } from "../components/ui/pagination";
import { PaginationState, RaciParty, PartyType } from "../types/index";
import { Card, CardContent } from "../components/ui/card";

export default function ActivitiesPage() {
  const { procedures, activities, users, jobRoles, externalPartners, searchActivities, activitiesData } = useData();
  const [procedureFilter, setProcedureFilter] = useState<string>("all");
  const [responsibleFilter, setResponsibleFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedActivities, setSearchedActivities] = useState<Activity[]>([]);
  const [searchPagination, setSearchPagination] = useState<PaginationState | null | undefined>(null);
  const [isSearching, setIsSearching] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);

  useEffect(() => {
    const fetchFilteredActivities = async () => {
      setSearchedActivities([]);
      try {
        setIsSearching(true);
        const result = await searchActivities({ name: searchTerm, procedureId: procedureFilter, userId: userFilter, responsibleUserId: responsibleFilter, limit: 25 });
        if (result.success && Array.isArray(result.activities)) {
          setSearchedActivities(result.activities);
          setSearchPagination(result.pagination);
        } else {
          setSearchedActivities([]);
        }
        // }

      } catch (error) {
        // console.error(error);
      } finally {
        setIsSearching(false);
      }
    }

    fetchFilteredActivities();
  }, [searchTerm, procedureFilter, userFilter, responsibleFilter])

  useEffect(() => {
    const totalPages = searchPagination?.pages || 0;
    setTotalPages(totalPages);
    const getPageNumbers = () => {
      const pageNumbers = [];
      const maxVisiblePages = 5;
      const halfVisible = Math.floor(maxVisiblePages / 2);

      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      return pageNumbers;
    };

    setPageNumbers(getPageNumbers());
  }, [searchPagination, searchedActivities])

  // Update handlers to reset page
  const handleProcedureFilter = (value: string) => {
    setProcedureFilter(value);
    setCurrentPage(1);
  };

  const handleResponsibleFilter = (value: string) => {
    setResponsibleFilter(value);
    setCurrentPage(1);
  };

  const handleUserFilter = (value: string) => {
    setUserFilter(value);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredActivities = searchedActivities;

  // Handle page changes
  const handlePageChange = async (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
    setIsSearching(true);
    setSearchedActivities([]);

    const result = await searchActivities({
      name: searchTerm,
      procedureId: procedureFilter,
      userId: userFilter,
      page,
      limit: 25
    });

    if (result.success && Array.isArray(result.activities)) {
      setSearchedActivities(result.activities);
      setSearchPagination(result.pagination);
      setIsSearching(false);
    } else {

      setSearchedActivities([]);
    }
  };

  const getProcedureName = (procedure: string | Procedure | undefined) => {
    if (!procedure) return "Unknown Procedure";
    if (typeof procedure === 'string') {
      const proc = procedures.find(p => p.id === procedure);
      return proc ? proc.procedureName : "Unknown Procedure";
    }
    return procedure.procedureName || "Unknown Procedure";
  };

  const getRaciAssignment = (party: RaciParty | null | undefined): string => {
    // If the party is null or undefined, return a default string.
    if (!party) {
      return "Not assigned";
    }

    // Use the party's type to determine where to look for the name.
    switch (party.type) {
      case PartyType.User: {
        // If the name is already on the object, use it.
        if (party.name) return party.name;
        // Otherwise, find the user in the main users list.
        const user = users.find(u => u.id == party.id);
        return user ? user.name : "Unknown User";
      }

      case PartyType.JobRole: {
        // If the role name is on the object, use it.
        if (party.roleName) return `${party.roleName} (Role)`;
        // Otherwise, find the job role in the main list.
        const jobRole = jobRoles.find(r => r.id == party.id);
        return jobRole ? `${jobRole.title} (Role)` : "Unknown Role";
      }

      case PartyType.ExternalPartner: {
        // If the name is on the object, use it.
        if (party.name) return `${party.name} (External)`;
        // Otherwise, find the partner in the main list.
        const partner = externalPartners.find(p => p.id == party.id);
        return partner ? `${partner.name} (External)` : "Unknown Partner";
      }

      // Fallback for any unexpected party types.
      default:
        return "Unknown Assignment";
    }
  };


  const getRaciDisplay = (activity: Activity) => {
    // Helper to render a list of parties from an array
    const renderPartyArray = (parties: RaciParty[] | undefined) => {
      if (!parties || parties.length === 0) {
        return "None";
      }
      return parties.map(party => getRaciAssignment(party)).join(", ");
    };

    return (
      <div className="flex flex-col gap-1 text-xs">
        {/* Responsible - Single */}
        <div>
          <span className="font-semibold">R:</span> {getRaciAssignment(activity.raci?.responsiblePerson)}
        </div>

        {/* Accountable - Single */}
        <div>
          <span className="font-semibold">A:</span> {getRaciAssignment(activity.raci?.accountablePerson)}
        </div>

        {/* Consulted - Array */}
        {activity.raci?.consultedPerson && activity.raci.consultedPerson.length > 0 && (
          <div>
            <span className="font-semibold">C:</span> {renderPartyArray(activity.raci.consultedPerson)}
          </div>
        )}

        {/* Informed - Array */}
        {activity.raci?.informedPerson && activity.raci.informedPerson.length > 0 && (
          <div>
            <span className="font-semibold">I:</span> {renderPartyArray(activity.raci.informedPerson)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 p-2 md:p-0">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Activities</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search activities..."
          value={searchTerm}
          onChange={handleSearch}
          className="pl-10"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select value={procedureFilter} onValueChange={handleProcedureFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by procedure" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Procedures</SelectItem>
              {procedures
                .filter((proc): proc is (typeof proc & { id: string }) => proc.id !== undefined)
                .map(proc => (
                  <SelectItem key={proc.id} value={proc.id}>
                    {proc.procedureName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select value={userFilter} onValueChange={handleUserFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by user (RACI)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name} ({user.jobRole})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={responsibleFilter} onValueChange={handleResponsibleFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by responsible" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Responsible</SelectItem>
              {users.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name} ({user.jobRole})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content Area */}
      <div className="space-y-4">
        {/* Mobile View: List of Cards */}
        <div className="md:hidden space-y-4">
          {isSearching ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : searchedActivities.length > 0 ? (
            searchedActivities.map((activity) => (
              <div key={activity.id} className="p-4 border rounded-lg">
                <div className="flex flex-col gap-3">
                  <Link to={`/activities/${activity.id}`} className="font-semibold text-primary hover:underline">
                    {activity.activityName}
                  </Link>
                  <div className="text-sm text-muted-foreground">
                    <strong>Procedure: </strong>
                    {(() => {
                      let procId = typeof activity.procedure === 'string' ? activity.procedure : activity.procedure?.id;
                      let procName = getProcedureName(activity.procedure);
                      return procId ? <Link to={`/procedure/${procId}`} className="text-primary hover:underline">{procName}</Link> : procName;
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground p-3 bg-gray-50 rounded-md">
                    {getRaciDisplay(activity)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              No activities found.
            </div>
          )}
        </div>

        {/* Desktop View: Table */}
        <Card className="hidden md:block">
          <CardContent className="p-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Activity Name</TableHead>
                  <TableHead>Procedure</TableHead>
                  <TableHead>RACI</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isSearching ? (
                  <TableRow>
                    <TableCell colSpan={3} className="h-48 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : searchedActivities.length > 0 ? (
                  searchedActivities.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <Link to={`/activities/${activity.id}`} className="font-medium text-primary hover:underline">
                          {activity.activityName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          let procId = typeof activity.procedure === 'string' ? activity.procedure : activity.procedure?.id;
                          let procName = getProcedureName(activity.procedure);
                          return procId ? <Link to={`/procedure/${procId}`} className="text-primary hover:underline">{procName}</Link> : procName;
                        })()}
                      </TableCell>
                      <TableCell>{getRaciDisplay(activity)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="h-48 text-center text-muted-foreground">
                      No activities found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <Button variant="ghost" size="icon" onClick={() => handlePageChange(1)} disabled={currentPage === 1}><ChevronsLeft className="h-4 w-4" /></Button>
              </PaginationItem>
              <PaginationItem>
                <Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
              </PaginationItem>
              {pageNumbers.map((pageNum) => (
                <PaginationItem key={pageNum}>
                  <Button variant={currentPage === pageNum ? "outline" : "ghost"} onClick={() => handlePageChange(pageNum)} className="size-9">{pageNum}</Button>
                </PaginationItem>
              ))}
              <PaginationItem>
                <Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button>
              </PaginationItem>
              <PaginationItem>
                <Button variant="ghost" size="icon" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}><ChevronsRight className="h-4 w-4" /></Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}